import { GetTravelSegmentResponse } from '@orbit/api-types'
import { Event, Trip } from '@types'
import { useCallback, useMemo } from 'react'
import { tripCalendar } from './core/calendar'
import { dayHeader, destinationSegments, spanIndexForDay } from './core/dayHeader'
import { destinationColorIndex, legendRows, tripSummary } from './core/destinations'
import { dayMapModel, tripMapModel } from './core/map'
import { pickSegmentRoute } from './segmentDisplay'

const NO_EVENTS: Event[] = []
const NO_SEGMENTS: GetTravelSegmentResponse[] = []
const NO_DESTINATIONS: Trip['destinations'] = []

/** The day info fields the card shows; both the member and public wires carry them. */
export interface DayInfoFields {
	dayIndex: number
	name: string | null
	note: string | null
}

const NO_DAY_INFOS: DayInfoFields[] = []

export interface ItineraryModelInput {
	/** Undefined while it loads, so the page can call this before its loading branch. */
	trip: Trip | undefined
	events: Event[] | undefined
	dayInfos: DayInfoFields[] | undefined
	segments: GetTravelSegmentResponse[] | undefined
	selectedDay: number
}

/**
 * Everything the itinerary card shows, derived from the trip's data and the
 * selected day. One derivation for both modes, so the member page and the
 * Shared trip view cannot drift apart.
 */
export const useItineraryModel = ({ trip, events, dayInfos, segments, selectedDay }: ItineraryModelInput) => {
	const allEvents = events ?? NO_EVENTS
	const allSegments = segments ?? NO_SEGMENTS
	const allDayInfos = dayInfos ?? NO_DAY_INFOS
	const dayCount = trip?.dayCount ?? 0
	const startDate = trip?.startDate
	const destinations = trip?.destinations ?? NO_DESTINATIONS

	// "Spans" here, because "segments" on this page are the travel segments between events.
	const destinationSpans = useMemo(() => (trip ? destinationSegments(trip) : []), [trip])
	const colorIndexOf = useCallback(
		(dayIndex: number) => destinationColorIndex(destinationSpans, dayIndex),
		[destinationSpans],
	)

	const eventsByDay = useMemo(() => {
		const byDay = new Map<number, Event[]>()
		for (const event of allEvents) {
			const list = byDay.get(event.tripDayIndex) ?? []
			list.push(event)
			byDay.set(event.tripDayIndex, list)
		}
		byDay.forEach(list => list.sort((a, b) => a.eventIndex - b.eventIndex))
		return byDay
	}, [allEvents])

	const dayEvents = eventsByDay.get(selectedDay) ?? NO_EVENTS
	const daysWithEvents = useMemo(() => new Set(eventsByDay.keys()), [eventsByDay])
	const daySegments = useMemo(() => allSegments.filter(s => s.tripDayIndex === selectedDay), [allSegments, selectedDay])
	const dayInfo = allDayInfos.find(info => info.dayIndex === selectedDay)
	const header = useMemo(
		() => dayHeader({ dayIndex: selectedDay, startDate: startDate, segments: destinationSpans, dayName: dayInfo?.name }),
		[selectedDay, startDate, destinationSpans, dayInfo?.name],
	)

	const calendar = useMemo(
		() => tripCalendar(startDate, dayCount, { selectedDay, daysWithEvents, colorIndexOf }),
		[startDate, dayCount, selectedDay, daysWithEvents, colorIndexOf],
	)

	const dayMap = useMemo(
		() =>
			dayMapModel({
				dayIndex: selectedDay,
				events: dayEvents,
				segments: daySegments,
				polylineOf: segment => pickSegmentRoute(segment)?.polyline ?? null,
				destinations: destinations,
			}),
		[selectedDay, dayEvents, daySegments, destinations],
	)
	const tripMap = useMemo(() => tripMapModel(allEvents, colorIndexOf), [allEvents, colorIndexOf])
	const mappedEventIds = useMemo(() => new Set(dayMap.pins.map(p => p.eventId)), [dayMap])

	const legend = useMemo(() => legendRows(destinations, startDate), [destinations, startDate])
	const activeSpanIndex = spanIndexForDay(destinationSpans, selectedDay)
	const colorIndex = colorIndexOf(selectedDay)
	const summary = useMemo(
		() => tripSummary({ startDate, dayCount, destinationCount: destinations.length }),
		[startDate, dayCount, destinations.length],
	)
	const dayOptions = useMemo(
		() =>
			Array.from({ length: dayCount }, (_, dayIndex) => {
				const h = dayHeader({ dayIndex, startDate: startDate, segments: destinationSpans, dayName: null })
				return { dayIndex, label: h.subtitle ? `${h.title} · ${h.subtitle}` : h.title }
			}),
		[dayCount, startDate, destinationSpans],
	)

	return {
		dayCount,
		selectedDay,
		eventsByDay,
		dayEvents,
		daySegments,
		dayInfo,
		header,
		calendar,
		dayMap,
		tripMap,
		mappedEventIds,
		legend,
		activeSpanIndex,
		colorIndex,
		summary,
		dayOptions,
	}
}

export type ItineraryModel = ReturnType<typeof useItineraryModel>
